import React, { useEffect, useState } from 'react';
import { ShoppingCart } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import ProductImage from './ProductImage';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useNavigate } from 'react-router-dom';
import { resolveProductImageUrl } from '../utils/cartLine';

const VariantPicker = ({ label, hint, variants, selectedId, disabledId, onSelect }) => (
  <div className="mb-3">
    <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1">{label}</p>
    {hint && <p className="text-xs text-neutral-500 mb-2">{hint}</p>}
    <div className="grid grid-cols-4 gap-2">
      {variants.map((variant) => {
        const selected = selectedId === variant.id;
        const disabled = disabledId === variant.id;
        return (
          <button
            key={variant.id}
            type="button"
            onClick={() => !disabled && onSelect(variant)}
            disabled={disabled}
            className={`rounded-lg border-2 p-1 transition-all text-left ${
              disabled
                ? 'cursor-not-allowed border-neutral-100 opacity-40'
                : selected
                  ? 'border-neutral-950 ring-2 ring-neutral-950/20'
                  : 'border-neutral-200 hover:border-neutral-400'
            }`}
            title={variant.name}
          >
            <div className="relative aspect-square w-full overflow-hidden rounded bg-neutral-100">
              <ProductImage
                key={variant.id}
                src={variant.image}
                alt={variant.name}
                priority
                className="rounded"
              />
            </div>
            <span className="mt-1 block truncate text-[10px] font-semibold text-neutral-800">
              {variant.name}
            </span>
          </button>
        );
      })}
    </div>
  </div>
);

const ProductCard = ({ product, priority = false }) => {
  const { addToCart } = useCart();
  const { isAuthenticated } = useCustomerAuth();
  const navigate = useNavigate();
  const soldOut = product.inStock === false || product.available === false || product.quantity === 0;
  const hasVariants = Array.isArray(product.variants) && product.variants.length > 0;
  const mainImageSrc = product.mainImage || product.image;
  const [primaryVariant, setPrimaryVariant] = useState(null);
  const [backupVariant, setBackupVariant] = useState(null);

  useEffect(() => {
    setPrimaryVariant(null);
    setBackupVariant(null);
  }, [product.id]);

  const displayImageSrc = primaryVariant ? primaryVariant.image : mainImageSrc;
  const displayImage = resolveProductImageUrl(displayImageSrc);

  const flavorsReady =
    !hasVariants ||
    (primaryVariant &&
      backupVariant &&
      primaryVariant.id !== backupVariant.id);

  const handlePrimarySelect = (variant) => {
    setPrimaryVariant(variant);
    if (backupVariant?.id === variant.id) {
      setBackupVariant(null);
    }
  };

  const handleAddToCart = () => {
    if (soldOut || !flavorsReady) return;
    if (!isAuthenticated) {
      navigate('/customer/login', { state: { from: window.location.pathname } });
      return;
    }
    addToCart({
      ...product,
      image: displayImage,
      selectedVariant: primaryVariant || undefined,
      backupVariant: backupVariant || undefined,
    });
  };

  const addButtonLabel = () => {
    if (soldOut) return 'Sold Out';
    if (!isAuthenticated) return 'Login to Add';
    if (hasVariants && !primaryVariant) return 'Choose primary flavor';
    if (hasVariants && !backupVariant) return 'Choose backup flavor';
    if (hasVariants && primaryVariant?.id === backupVariant?.id) return 'Choose different backup';
    return 'Add to Cart';
  };

  return (
    <div
      className={`group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden transform hover:-translate-y-1 border ${
        soldOut ? 'opacity-90' : ''
      }`}
    >
      <div className="relative aspect-square overflow-hidden bg-neutral-100">
        <ProductImage
          key={displayImageSrc}
          src={displayImageSrc}
          alt={
            primaryVariant
              ? `${product.name} — ${primaryVariant.name}`
              : product.name
          }
          priority={priority}
          soldOut={soldOut}
          className={soldOut ? '' : 'group-hover:scale-110 transition-transform duration-500'}
        />
        {soldOut ? (
          <Badge className="absolute top-3 right-3 bg-red-600 text-white">Sold Out</Badge>
        ) : (
          <>
            {product.quantity > 0 && product.quantity <= 3 && (
              <Badge className="absolute top-3 left-3 bg-amber-500 text-white">
                Only {product.quantity} left
              </Badge>
            )}
          </>
        )}
      </div>
      <div className="p-4">
        <p className="text-sm text-neutral-800 font-semibold mb-1">{product.category}</p>
        <h3 className="font-bold text-lg mb-2 text-neutral-900">{product.name}</h3>
        <p className="text-sm text-neutral-600 mb-3 line-clamp-2">{product.description}</p>

        {hasVariants && (
          <div className="mb-4 rounded-lg border border-neutral-200 bg-neutral-50 p-3">
            <VariantPicker
              label="Primary flavor"
              hint="Tap a flavor to preview it above."
              variants={product.variants}
              selectedId={primaryVariant?.id}
              disabledId={null}
              onSelect={handlePrimarySelect}
            />
            <VariantPicker
              label="Backup flavor"
              hint="Pick a second choice if your primary flavor is unavailable."
              variants={product.variants}
              selectedId={backupVariant?.id}
              disabledId={primaryVariant?.id || null}
              onSelect={setBackupVariant}
            />
          </div>
        )}

        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-neutral-700">THC: {product.thc}</span>
          <span className="text-xl font-bold text-neutral-900">${product.price?.toFixed(2)}</span>
        </div>
        <Button
          className={`w-full ${
            soldOut || !flavorsReady
              ? 'bg-neutral-300 text-neutral-600 cursor-not-allowed hover:bg-neutral-300'
              : 'bg-neutral-950 hover:bg-black text-white'
          }`}
          onClick={handleAddToCart}
          disabled={soldOut || !flavorsReady}
        >
          <ShoppingCart className="w-4 h-4 mr-2" />
          {addButtonLabel()}
        </Button>
      </div>
    </div>
  );
};

export default ProductCard;
